import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Mail, Phone, Lock, Key, MapPin, Monitor, ChevronRight, AlertCircle, CheckCircle, Smartphone, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import resumeAPI from '../services/api';

const SecuritySettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [devices, setDevices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      // Load user's security settings
      // This would call your backend API
      // For now, we'll use mock data
      
      // Check if 2FA is enabled
      const twoFAStatus = localStorage.getItem(`twoFA_${user?.user_id}`) === 'enabled';
      setTwoFactorEnabled(twoFAStatus);

      // Load devices
      const savedDevices = JSON.parse(localStorage.getItem(`devices_${user?.user_id}`) || '[]');
      setDevices(savedDevices);

      // Load active sessions
      const savedSessions = JSON.parse(localStorage.getItem(`sessions_${user?.user_id}`) || '[]');
      setSessions(savedSessions);
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const securityItems = [
    {
      id: 'email',
      icon: Mail,
      title: 'Email addresses',
      description: user?.email || 'Not set',
      link: '/settings/security/email',
      value: user?.email
    },
    {
      id: 'phone',
      icon: Phone,
      title: 'Phone numbers',
      description: 'Add a recovery phone',
      link: '/settings/security/phone',
      value: user?.phone || 'Not set'
    },
    {
      id: 'password',
      icon: Lock,
      title: 'Change password',
      description: 'Last changed: Never',
      link: '/settings/security/change-password'
    },
    {
      id: 'passkeys',
      icon: Key,
      title: 'Passkeys',
      description: 'Use your face, fingerprint, or screen lock',
      link: '/settings/security/passkeys',
      badge: 'New'
    },
    {
      id: 'signed-in',
      icon: MapPin,
      title: "Where you're signed in",
      description: `${sessions.length || 0} active ${sessions.length === 1 ? 'session' : 'sessions'}`,
      link: '/settings/security/sessions'
    },
    {
      id: 'devices',
      icon: Monitor,
      title: 'Devices that remember your password',
      description: `${devices.length || 0} ${devices.length === 1 ? 'device' : 'devices'}`,
      link: '/settings/security/devices'
    },
    {
      id: 'two-factor',
      icon: Shield,
      title: 'Two-factor authentication',
      description: twoFactorEnabled ? 'Enabled' : 'Add an extra layer of security',
      link: '/settings/security/two-factor',
      status: twoFactorEnabled ? 'On' : 'Off',
      statusColor: twoFactorEnabled ? 'text-green-600' : 'text-gray-400'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-primary-600 p-3 rounded-full">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Security Settings</h1>
              <p className="text-gray-600">Manage your account security and sign-in options</p>
            </div>
          </div>
        </div>

        {/* Account access section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Account access</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage how you sign in and keep your account secure
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {securityItems.map((item) => (
              <Link
                key={item.id}
                to={item.link}
                className="block px-6 py-4 hover:bg-gray-50 transition-colors duration-150"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex-shrink-0">
                      <item.icon className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-gray-900">
                          {item.title}
                        </h3>
                        {item.badge && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {item.value || item.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    {item.status && (
                      <span className={`text-sm font-medium ${item.statusColor}`}>
                        {item.status}
                      </span>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Security recommendations */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Security recommendations</h2>
          </div>
          <div className="p-6">
            {!twoFactorEnabled ? (
              <div className="flex items-start gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 mb-1">
                    Enable two-factor authentication
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Add an extra layer of security to your account by requiring a verification code when signing in.
                  </p>
                  <Link
                    to="/settings/security/two-factor"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    Enable Two-Factor Auth
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">
                    Your account is well protected
                  </h3>
                  <p className="text-sm text-gray-600">
                    Two-factor authentication is enabled and helping keep your account secure.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="bg-white rounded-lg shadow-sm border border-red-200 mt-6">
          <div className="px-6 py-4 border-b border-red-200 bg-red-50">
            <h2 className="text-xl font-semibold text-red-900">Danger Zone</h2>
            <p className="text-sm text-red-700 mt-1">
              Irreversible actions that affect your entire account
            </p>
          </div>
          <div className="p-6">
            <div className="flex items-start gap-4">
              <Trash2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Delete your account</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Once you delete your account, there is no going back. This will permanently:
                </p>
                <ul className="text-sm text-gray-600 mb-4 space-y-1 ml-4">
                  <li>• Delete your profile and all associated data</li>
                  <li>• Remove all your resumes and portfolios</li>
                  <li>• Remove your account from AWS Cognito</li>
                  <li>• Clear all your browser data</li>
                </ul>
                {deleteError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{deleteError}</p>
                  </div>
                )}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-red-100 p-3 rounded-full">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Delete Account Permanently?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This action <strong className="text-red-600">CANNOT be undone</strong>. 
                    Your account <strong>{user?.email}</strong> and all your data will be permanently deleted from our servers.
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-xs text-red-800 font-medium">
                      ⚠️ This will delete:
                    </p>
                    <ul className="text-xs text-red-700 mt-2 space-y-1 ml-4">
                      <li>• Your AWS Cognito account</li>
                      <li>• All profile data from DynamoDB</li>
                      <li>• All resumes and portfolios</li>
                      <li>• All browser cached data</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteError(null);
                  }}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Yes, Delete Forever'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Back link */}
        <div className="mt-6">
          <Link
            to="/settings"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Settings
          </Link>
        </div>
      </div>
    </div>
  );

  async function handleDeleteAccount() {
    setDeleteLoading(true);
    setDeleteError(null);

    try {
      console.log('[SecuritySettings] Starting account deletion for:', user?.email);
      console.log('[SecuritySettings] User ID:', user?.user_id);
      
      const result = await resumeAPI.deleteAccount();
      
      console.log('[SecuritySettings] ✅ Account deleted successfully:', result);
      
      // Close modal
      setShowDeleteConfirm(false);
      
      // Show success message
      const successMsg = `✅ Account Successfully Deleted!\n\n` +
        `Email: ${user?.email}\n` +
        `Mode: ${result.mode || 'AWS'}\n` +
        `${result.deletedResumes ? `Resumes deleted: ${result.deletedResumes}\n` : ''}` +
        `${result.message || 'All data permanently removed'}`;
      
      alert(successMsg);
      
      // Logout and redirect to home
      console.log('[SecuritySettings] Logging out and redirecting...');
      logout();
      navigate('/', { replace: true });
      
    } catch (error) {
      console.error('[SecuritySettings] ❌ Delete failed:', error);
      console.error('[SecuritySettings] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      const errorMsg = error.message || 'Failed to delete account. Please try again.';
      const detailedError = error.code ? `${errorMsg} (Code: ${error.code})` : errorMsg;
      
      setDeleteError(detailedError);
      setDeleteLoading(false);
    }
  }
};

export default SecuritySettingsPage;
